// AICA Input Component - Meridian Dark Design System
// Styled text input with icon support and enhanced focus states

import * as React from 'react';
import { cn } from '@/lib/utils';
import { CONTROL_HEIGHT } from './control-size';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Optional icon to display on the left side
   */
  icon?: React.ReactNode;
  /**
   * Optional icon to display on the right side
   */
  iconRight?: React.ReactNode;
  /**
   * Error state
   */
  error?: boolean;
  /**
   * Input size variant
   */
  inputSize?: 'sm' | 'md' | 'lg';
  /**
   * ID of the error message element for aria-describedby
   */
  errorId?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, icon, iconRight, error, inputSize = 'md', errorId, ...props },
    ref
  ) => {
    const sizes = {
      // Heights come from CONTROL_HEIGHT (sm 32, md 36, lg 40) so mixed controls line up.
      sm: cn(CONTROL_HEIGHT.sm, 'px-2 text-xs'),
      md: cn(CONTROL_HEIGHT.md, 'px-3 text-sm'),
      lg: cn(CONTROL_HEIGHT.lg, 'px-4 text-input-lg'),
    };

    // Use tabular-nums for numeric inputs
    const isNumeric = type === 'number' || type === 'tel';

    return (
      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle">
            {icon}
          </div>
        )}
        <input
          type={type}
          className={cn(
            // Base styles - Meridian Dark
            'flex w-full rounded-md border text-fg',
            'bg-bg-surface', // Surface background for inputs
            'border-border', // Subtle border
            'placeholder:text-fg-subtle', // Subtle placeholder text

            // Focus states - Meridian teal ring with glow
            'focus:border-primary focus:outline-none',
            'focus:ring-2 focus:ring-primary/30',
            'focus:shadow-glow-subtle',

            // Hover state
            'hover:border-border-emphasis',

            // Disabled state
            'disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:opacity-50',

            // Error state - danger color with red glow
            error && [
              'border-danger',
              'focus:border-danger focus:ring-danger/30',
              'focus:shadow-glow-error',
            ],

            // Transitions
            'transition-all duration-150 ease-standard',
            'motion-reduce:transition-none',

            // Size variants
            sizes[inputSize],

            // Icon padding adjustments
            icon &&
              (inputSize === 'sm' ? 'pl-8' : inputSize === 'lg' ? 'pl-12' : 'pl-10'),
            iconRight &&
              (inputSize === 'sm' ? 'pr-8' : inputSize === 'lg' ? 'pr-12' : 'pr-10'),

            // Tabular nums for numeric inputs
            isNumeric && 'tabular-nums',

            className
          )}
          ref={ref}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error && errorId ? errorId : undefined}
          {...props}
        />
        {iconRight && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-fg-subtle">
            {iconRight}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
